import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import PageWrapper from '../components/PageWrapper';

const TermsPage = () => {
  const navigate = useNavigate();
  const effectiveDate = "January 19, 2026";

  return (
    <PageWrapper>
      <button 
        onClick={() => navigate(-1)} 
        className="back-btn"
        data-testid="back-btn"
      >
        ← Back
      </button>

      <article className="legal-page" data-testid="terms-page">
        <h1>Terms of Use</h1>
        <p className="effective-date"><strong>Effective Date:</strong> {effectiveDate}</p>

        <div className="legal-content">
          <p>
            Please read these Terms of Use ("Terms") carefully before using Uro2Go.
          </p>

          <p>
            By accessing or using the Uro2Go website, application, or any related services 
            (collectively, the "Platform"), you agree to be legally bound by these Terms. 
            If you do not agree, do not access or use the Platform.
          </p>

          <h2>1. Educational Platform Only</h2>
          <p>
            Uro2Go is an educational and informational platform only. The Platform does not 
            provide medical advice, medical diagnosis, or medical treatment.
          </p>
          <p>
            All content is general in nature and is not a substitute for professional medical 
            evaluation or care.
          </p>

          <h2>2. No Physician–Patient Relationship</h2>
          <p>
            Use of the Platform does not create a physician–patient relationship or any other 
            professional relationship between you and Uro2Go, its owners, operators, contributors, 
            or affiliated clinicians.
          </p>

          <h2>3. Not for Emergencies</h2>
          <div className="warning-box">
            <AlertTriangle size={20} />
            <div>
              <p>
                The Platform is not intended for use in medical emergencies. If you are experiencing 
                a medical emergency, call 911 or your local emergency number immediately.
              </p>
            </div>
          </div>

          <h2>4. User Responsibilities</h2>
          <p>You agree that:</p>
          <ul className="legal-list">
            <li>You will not rely on the Platform for medical decisions.</li>
            <li>You are solely responsible for your healthcare decisions.</li>
            <li>You will seek care from a licensed clinician for diagnosis and treatment.</li>
            <li>You understand medical information may be incomplete or outdated.</li>
          </ul>

          <h2>5. No Warranties</h2>
          <p>
            The Platform and all content are provided "as is" and "as available," without warranties 
            of any kind, express or implied, including but not limited to warranties of accuracy, 
            reliability, merchantability, fitness for a particular purpose, or non-infringement.
          </p>

          <h2>6. Limitation of Liability</h2>
          <p>
            To the fullest extent permitted by law, Uro2Go shall not be liable for any direct, 
            indirect, incidental, consequential, special, exemplary, or punitive damages arising 
            from or related to your use of or inability to use the Platform or Content.
          </p>
          <p>
            This includes, without limitation, damages related to personal injury, health outcomes, 
            lost profits, lost data, or business interruption.
          </p>

          <h2>7. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless Uro2Go and its owners, officers, 
            employees, contractors, contributors, and affiliates from any claims, liabilities, 
            damages, losses, or expenses arising out of your use of the Platform or violation 
            of these Terms.
          </p>

          <h2>8. Third-Party Content and Links</h2>
          <p>
            The Platform may contain links to third-party websites or resources. Uro2Go does not 
            control and is not responsible for third-party content, policies, or practices.
          </p>

          <h2>9. Prohibited Uses</h2>
          <p>You agree not to:</p>
          <ul className="legal-list">
            <li>Use the Platform as a substitute for medical care</li>
            <li>Attempt to obtain personalized medical advice</li>
            <li>Upload false, misleading, or harmful content</li>
            <li>Reverse engineer or scrape the Platform</li>
            <li>Use the Platform for unlawful purposes</li>
            <li>Misrepresent your identity or credentials</li>
          </ul>

          <h2>10. Arbitration Agreement (IMPORTANT)</h2>
          <div className="important-notice">
            <p>
              Any dispute, claim, or controversy arising out of or relating to your use of the 
              Platform shall be resolved by binding individual arbitration, not in court.
            </p>
            <p>
              Arbitration shall be conducted under the rules of the American Arbitration Association.
            </p>
            <p>
              <strong>You waive any right to a jury trial.</strong>
            </p>
          </div>

          <h2>11. Class Action Waiver</h2>
          <p>
            You agree that all claims must be brought in your individual capacity only and not as 
            a plaintiff or class member in any purported class, collective, or representative proceeding.
          </p>

          <h2>12. Governing Law</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the 
            State of Illinois, without regard to conflict-of-law principles.
          </p>

          <h2>13. Changes to These Terms</h2>
          <p>
            Uro2Go may update these Terms at any time. Continued use of the Platform constitutes 
            acceptance of the revised Terms.
          </p>

          <h2>14. Contact</h2>
          <p>For questions regarding these Terms:</p>
          <div className="contact-info">
            <p><strong>Email:</strong> <a href="mailto:uro2go@gmail.com">uro2go@gmail.com</a></p>
          </div>
        </div>
      </article>
    </PageWrapper>
  );
};

export default TermsPage;
